import { ref, onUnmounted, type Ref } from 'vue'

/**
 * useSmokeCanvas - Realistic canvas-based smoke effect
 *
 * Creates magical wisp-style smoke rising from the bottom of a container.
 * Uses a particle system with turbulent drift for realistic fluid dynamics.
 *
 * ★ Insight ─────────────────────────────────────
 * Smoke realism comes from three factors:
 * 1. Perlin-like noise for organic horizontal drift
 * 2. Particles growing in size as they rise (like real smoke diffusing)
 * 3. Opacity fading smoothly to transparent
 * ─────────────────────────────────────────────────
 */

interface SmokeParticle {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  opacity: number
  life: number
  maxLife: number
  noiseOffset: number // For organic drift
}

export function useSmokeCanvas(container: Ref<HTMLElement | null>) {
  const canvas = ref<HTMLCanvasElement | null>(null)
  const ctx = ref<CanvasRenderingContext2D | null>(null)
  const particles = ref<SmokeParticle[]>([])
  const animationId = ref<number | null>(null)
  const isRunning = ref(false)

  // Configuration
  const config = {
    maxParticles: 80,
    spawnRate: 5, // Particles per frame
    baseSpeed: 2.5,
    turbulence: 0.4,
    growthRate: 1.015,
    fadeRate: 0.003,
    minLife: 200,
    maxLife: 350,
    baseSize: 20,
    maxSize: 120,
    // Trophy collision zone (relative to canvas center)
    trophyZone: {
      width: 120,
      height: 180,
      yOffset: 0.4, // 40% from top
    },
    // Colors: bright white with warm fire tint at bottom
    colors: {
      top: 'rgba(240, 240, 255, ', // Bright white at top
      bottom: 'rgba(255, 220, 180, ', // Warm golden at bottom
    },
  }

  /**
   * Simple noise function for organic drift
   * Uses sine waves at different frequencies
   */
  function noise(x: number, offset: number): number {
    return (
      Math.sin(x * 0.01 + offset) * 0.5 +
      Math.sin(x * 0.023 + offset * 1.3) * 0.3 +
      Math.sin(x * 0.041 + offset * 0.7) * 0.2
    )
  }

  /**
   * Initialize the canvas
   */
  function init(): void {
    if (!container.value) return

    // Create canvas element
    const canvasEl = document.createElement('canvas')
    canvasEl.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    `

    container.value.appendChild(canvasEl)
    canvas.value = canvasEl

    // Set canvas size
    resize()

    // Get context
    const context = canvasEl.getContext('2d')
    if (context) {
      ctx.value = context
    }

    // Listen for resize
    window.addEventListener('resize', resize)
  }

  /**
   * Resize canvas to match container
   */
  function resize(): void {
    if (!canvas.value || !container.value) return

    const rect = container.value.getBoundingClientRect()
    canvas.value.width = rect.width
    canvas.value.height = rect.height
  }

  /**
   * Spawn a new smoke particle
   */
  function spawnParticle(): void {
    if (!canvas.value) return

    const width = canvas.value.width
    const height = canvas.value.height

    // Spawn at bottom center with some spread
    const centerX = width / 2
    const spreadX = width * 0.15

    const particle: SmokeParticle = {
      x: centerX + (Math.random() - 0.5) * spreadX,
      y: height + 20, // Start below visible area
      vx: (Math.random() - 0.5) * 1.0, // More horizontal spread
      vy: -(config.baseSpeed + Math.random() * 1.0), // Faster upward
      size: config.baseSize + Math.random() * 10,
      opacity: 0.5 + Math.random() * 0.3,
      life: 0,
      maxLife: config.minLife + Math.random() * (config.maxLife - config.minLife),
      noiseOffset: Math.random() * 1000,
    }

    particles.value.push(particle)
  }

  /**
   * Update particle physics
   */
  function updateParticle(particle: SmokeParticle): boolean {
    if (!canvas.value) return false

    particle.life++

    // Check if particle should die (also check if off screen top)
    if (particle.life >= particle.maxLife || particle.opacity <= 0 || particle.y < -50) {
      return false // Remove this particle
    }

    const width = canvas.value.width
    const height = canvas.value.height

    // Calculate life progress (0 to 1)
    const progress = particle.life / particle.maxLife

    // Trophy collision zone
    const trophyCenterX = width / 2
    const trophyCenterY = height * config.trophyZone.yOffset
    const trophyLeft = trophyCenterX - config.trophyZone.width / 2
    const trophyRight = trophyCenterX + config.trophyZone.width / 2
    const trophyTop = trophyCenterY - config.trophyZone.height / 2
    const trophyBottom = trophyCenterY + config.trophyZone.height / 2

    // Check if particle is in trophy zone
    const inTrophyZone =
      particle.x > trophyLeft - particle.size &&
      particle.x < trophyRight + particle.size &&
      particle.y > trophyTop - particle.size &&
      particle.y < trophyBottom + particle.size

    if (inTrophyZone) {
      // Deflect around trophy - push outward and swirl
      const dx = particle.x - trophyCenterX
      const dy = particle.y - trophyCenterY
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist > 0) {
        // Push away from center
        const pushStrength = 0.3
        particle.vx += (dx / dist) * pushStrength

        // Add swirl effect (perpendicular to center)
        const swirlStrength = 0.15
        particle.vx += (-dy / dist) * swirlStrength * (dx > 0 ? 1 : -1)
        particle.vy += (dx / dist) * swirlStrength * 0.5
      }

      // Speed up vertical movement to flow around
      particle.vy *= 1.02
    }

    // Turbulent drift using noise
    const driftAmount = noise(particle.y, particle.noiseOffset) * config.turbulence
    particle.vx += driftAmount * 0.1
    particle.vx *= 0.98 // Damping

    // Apply velocity
    particle.x += particle.vx
    particle.y += particle.vy

    // Slow down vertical speed as it rises (but not as much)
    particle.vy *= 0.998

    // Grow in size (smoke diffuses as it rises)
    particle.size *= config.growthRate
    if (particle.size > config.maxSize) {
      particle.size = config.maxSize
    }

    // Fade out based on life progress
    // Quick fade in at start, steady, then quick fade at end
    if (progress < 0.1) {
      // Fade in
      particle.opacity = Math.min(0.7, particle.opacity + 0.05)
    } else if (progress > 0.8) {
      // Fade out near top
      particle.opacity -= config.fadeRate * 2
    } else {
      // Very gentle fade
      particle.opacity -= config.fadeRate * 0.5
    }

    return true // Keep this particle
  }

  /**
   * Draw a single particle
   */
  function drawParticle(particle: SmokeParticle): void {
    if (!ctx.value || !canvas.value) return

    const context = ctx.value
    const height = canvas.value.height

    // Calculate color based on height (warmer at bottom)
    const heightProgress = 1 - particle.y / height
    const warmth = Math.max(0, 1 - heightProgress * 1.5)

    // Blend between warm (bottom) and cool (top) colors
    const r = Math.round(180 + warmth * 75)
    const g = Math.round(180 + warmth * 20)
    const b = Math.round(190 - warmth * 40)

    // Draw soft circular gradient for smoke puff
    const gradient = context.createRadialGradient(
      particle.x,
      particle.y,
      0,
      particle.x,
      particle.y,
      particle.size
    )

    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${particle.opacity * 0.8})`)
    gradient.addColorStop(0.4, `rgba(${r}, ${g}, ${b}, ${particle.opacity * 0.3})`)
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)

    context.fillStyle = gradient
    context.beginPath()
    context.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
    context.fill()
  }

  /**
   * Main animation loop
   */
  function animate(): void {
    if (!isRunning.value || !ctx.value || !canvas.value) return

    const context = ctx.value
    const width = canvas.value.width
    const height = canvas.value.height

    // Clear canvas
    context.clearRect(0, 0, width, height)

    // Spawn new particles
    for (let i = 0; i < config.spawnRate; i++) {
      if (particles.value.length < config.maxParticles) {
        spawnParticle()
      }
    }

    // Update and draw particles
    particles.value = particles.value.filter((particle) => {
      const alive = updateParticle(particle)
      if (alive) {
        drawParticle(particle)
      }
      return alive
    })

    // Continue animation
    animationId.value = requestAnimationFrame(animate)
  }

  /**
   * Start the smoke effect
   */
  function start(): void {
    if (isRunning.value) return

    init()
    isRunning.value = true
    animate()
  }

  /**
   * Stop the smoke effect
   */
  function stop(): void {
    isRunning.value = false

    if (animationId.value) {
      cancelAnimationFrame(animationId.value)
      animationId.value = null
    }
  }

  /**
   * Cleanup
   */
  function destroy(): void {
    stop()
    window.removeEventListener('resize', resize)

    if (canvas.value && canvas.value.parentNode) {
      canvas.value.parentNode.removeChild(canvas.value)
    }

    canvas.value = null
    ctx.value = null
    particles.value = []
  }

  // Auto cleanup on unmount
  onUnmounted(() => {
    destroy()
  })

  return {
    start,
    stop,
    destroy,
    isRunning,
  }
}
